import { useMemo } from "react";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import { useTheme } from "@mui/material/styles";

import brandLogo from "assets/images/logos/HeaderLogo.svg";
import { makeStyles } from "tss-react/mui";
import { styles } from "./Header.styles";
import SelectLanguage from "components/SelectLanguage/SelectLaguage";

import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ProgressWidget from "components/ProgressWidget/ProgressWidget";
import { useAppSelector } from "store/hooks";
import { getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { detectHmo } from "utils/hmoDetection";
import { HMO } from "constants/hmo";

interface HeaderProps {
  currentStep: number;
}

type descriptionType = "TEXT" | "CONTAINER" | "STEPS" | "LARGE_TEXT" | "SHORT";

interface StepsHeaderContent {
  [key: number]: {
    descriptionType: descriptionType;
    logo: string;
    title?: string;
    description?: string[];
    stepWidgetProgress?: number;
  };
}
const VERSION = import.meta.env.VITE_VERSION;

const Header = ({ currentStep }: HeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const testType = useAppSelector(getTestTypeValue);
  const currentHmo = detectHmo();
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  
  const useStyles = makeStyles()((theme) => styles(theme));
  const { classes } = useStyles();
  const isModelsLoaded = useAppSelector(state => state.imageRecognition.isModelsLoaded);
  const isClinicalTrialHmo = currentHmo === HMO.CLINICAL_TRIAL;
  
  // Get HMO logo and name from translations if they exist
  const hmoLogoKey = currentHmo ? `common:hmo:${currentHmo}:logo` : null;
  const hmoNameKey = currentHmo ? `common:hmo:${currentHmo}:name` : null;
  
  const hmoLogo = useMemo(() => {
    if (!hmoLogoKey) return null;
    const logo = t(hmoLogoKey, { defaultValue: null });
    return logo !== hmoLogoKey ? logo : null;
  }, [hmoLogoKey, t]);
  
  const hmoName = useMemo(() => {
    if (!hmoNameKey) return null;
    const name = t(hmoNameKey, { defaultValue: null });
    return name !== hmoNameKey ? name : null;
  }, [hmoNameKey, t]);

  const StepsHeaderContent: StepsHeaderContent = useMemo(() => ({
    0: {
      title: `${testTypeLocaleKey}:header:step1:title`,
      description: [`${testTypeLocaleKey}:header:step1:description1`, `${testTypeLocaleKey}:header:step1:description2`],
      descriptionType: "TEXT",
      logo: brandLogo,
    },
    1: {
      title: `${testTypeLocaleKey}:header:step2:title`,
      description: [`${testTypeLocaleKey}:header:step2:description`],
      descriptionType: "CONTAINER",
      logo: brandLogo,
    },
    2: {
      title: `${testTypeLocaleKey}:header:step3:title`,
      descriptionType: "STEPS",
      stepWidgetProgress: 1,
      logo: brandLogo,
    },
    3: {
      title: `${testTypeLocaleKey}:header:step3:title`,
      description: [""],
      descriptionType: "STEPS",
      stepWidgetProgress: 2,
      logo: brandLogo,
    },
    4: {
      title: `${testTypeLocaleKey}:header:step3:title`,
      description: [""],
      descriptionType: "STEPS",
      stepWidgetProgress: 3,
      logo: brandLogo,
    },
    5: {
      title: isClinicalTrialHmo ? "SelfReportScreen:title" : `${testTypeLocaleKey}:header:step4:title`,
      descriptionType: "LARGE_TEXT",
      logo: brandLogo,
    },
    6: {
      descriptionType: "SHORT",
      logo: brandLogo,
    },
  }), [testTypeLocaleKey, isClinicalTrialHmo]);

  return (
    <Card style={{ width: theme.width.fullVw }}>
      <div
        className={classNames(
          StepsHeaderContent[currentStep].descriptionType === "SHORT"
          ? classes.headerContainer_SHORT
          : classes.headerContainer,
          classes.headerContainerBG
          )}
      >
        <div className={classes.versionText}>{VERSION}</div>
        <div className={classes.logoRow}>
          <MDBox
            component="img"
            className={classes.brandLogo}
            src={brandLogo}
            alt="Brand"
          />
          <div className={classes.languageWrapper}>
            <SelectLanguage />
            <div className={classes.loadingCircle} style={{ background: isModelsLoaded ? theme.palette.success.main : theme.palette.error.main }}></div>
          </div>
        </div>
        {currentStep === 0 && (hmoLogo || hmoName) && (
          <div className={classes.hmoSection}>
            {hmoLogo && (
              <MDBox
                component="img"
                className={classes.hmoLogo}
                src={hmoLogo}
                alt="HMO Logo"
              />
            )}
            {hmoName && (
              <div className={classes.hmoName}>
                {hmoName}
              </div>
            )}
          </div>
        )}
        <div
          className={classNames(
            (currentStep === 0 && hmoLogo) ? classes.noPaddingTop:classes.titleWithMargin,
            (
           ( StepsHeaderContent[currentStep].descriptionType === "LARGE_TEXT"
              ? classes.largeTitle
              : classes.title)
            ),
          )}
        >
          {t(StepsHeaderContent[currentStep].title)}
        </div>
        <div className={classNames(
          (currentStep === 0 && hmoLogo) ? classes.noPaddingTop:classes.descriptionWithMargin,
          classes.descriptionSection
        )}>
          {StepsHeaderContent[currentStep].descriptionType === "TEXT" && (
            <div className={classes.descriptionBoxText}>
              {StepsHeaderContent[currentStep].description?.map(
                (description, index) => (
                  <div key={index} className={classes.description}>
                    {t(description)}
                  </div>
                )
              )}
            </div>
          )}
          {StepsHeaderContent[currentStep].descriptionType === "CONTAINER" && (
            <div className={classes.descriptionBoxContainer}>
              {t(StepsHeaderContent[currentStep].description || "")}
            </div>
          )}
          {StepsHeaderContent[currentStep].descriptionType === "STEPS" && (
            <ProgressWidget
              currentProgressStep={
                StepsHeaderContent[currentStep].stepWidgetProgress
              }
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default Header;
